/**
 * Environment Configuration
 * 
 * Centralizes environment configuration following Module M7 (Integration Points)
 * from the ConnectPath Modular Methodology.
 */

export interface EnvironmentConfig {
  // Application settings
  appName: string;
  appVersion: string;
  environment: 'development' | 'staging' | 'production' | 'test';

  // API settings
  apiUrl: string;
  wsUrl: string; // WebSocket URL
  cdnUrl: string; // CDN URL for static assets
  apiTimeout: number;

  // Feature flags
  enableDebugLogging: boolean;
  enableAnalytics: boolean;
  enableOfflineMode: boolean;
  useMockData: boolean;
  
  // Authentication settings
  authTokenKey: string;
  refreshTokenKey: string;
  // Session timeout settings are in EnhancedTokenManager.ts (SSOT)
  // - Idle timeout: 30 minutes
  // - Warn before: 5 minutes
  
  // Storage settings
  localStoragePrefix: string;
  sessionStoragePrefix: string;
  
  // Performance settings
  cacheTimeout: number; // milliseconds
  maxRetries: number;
  retryDelay: number; // milliseconds
  
  // Security settings
  enableHttps: boolean;
  csrfCookieName: string;
  
  // Dashboard defaults
  defaultTimeRange: string;
  defaultDashboardRefreshInterval: number; // milliseconds
}

// Get environment from Vite env variables
const env = import.meta.env.VITE_ENV || 'development';

// Default configuration values
const defaultConfig: EnvironmentConfig = {
  appName: 'ConnectPath',
  appVersion: import.meta.env.VITE_VERSION || '1.0.0',
  environment: env as any,
  
  // API settings - Use relative URLs by default (nginx handles routing)
  apiUrl: import.meta.env.VITE_API_URL || '/api/v1',
  wsUrl: import.meta.env.VITE_WS_URL || '/ws',
  cdnUrl: import.meta.env.VITE_CDN_URL || '',
  apiTimeout: parseInt(import.meta.env.VITE_API_TIMEOUT || '30000'),
  
  // Feature flags - can be overridden by env variables
  // Note: All data comes from real backend APIs - no mock services
  enableDebugLogging: import.meta.env.VITE_DEBUG === 'true',
  enableAnalytics: import.meta.env.VITE_ENABLE_ANALYTICS === 'true',
  enableOfflineMode: import.meta.env.VITE_ENABLE_OFFLINE === 'true',
  useMockData: import.meta.env.VITE_USE_MOCK_DATA === 'true',
  
  // Authentication settings
  authTokenKey: 'connectpath_access_token',
  refreshTokenKey: 'connectpath_refresh_token',
  // Note: Session timeout config is in EnhancedTokenManager.ts (30 min idle)
  
  // Storage settings
  localStoragePrefix: 'connectpath_',
  sessionStoragePrefix: 'connectpath_session_',
  
  // Performance settings
  cacheTimeout: 5 * 60 * 1000, // 5 minutes
  maxRetries: 3,
  retryDelay: 1000, // 1 second
  
  // Security settings
  enableHttps: env === 'production',
  csrfCookieName: 'csrftoken',
  
  // Dashboard defaults
  defaultTimeRange: '30d',
  defaultDashboardRefreshInterval: 60000, // 1 minute
};

// Environment-specific overrides
const environmentOverrides: Partial<Record<string, Partial<EnvironmentConfig>>> = {
  development: {
    enableDebugLogging: true,
    enableAnalytics: false,
    // Use environment variable - no hardcoding
    apiUrl: import.meta.env.VITE_API_URL || '/api/v1',
  },
  staging: {
    enableDebugLogging: true,
    enableAnalytics: true,
  },
  production: {
    enableDebugLogging: false,
    enableAnalytics: true,
    // Production uses relative URLs via nginx proxy
    apiUrl: import.meta.env.VITE_API_URL || '/api/v1',
    wsUrl: import.meta.env.VITE_WS_URL || '/ws',
    cdnUrl: import.meta.env.VITE_CDN_URL || '',
    enableHttps: true,
  },
  test: {
    enableDebugLogging: false,
    enableAnalytics: false,
    apiTimeout: 5000,
  },
};

// Merge configurations
export const config: EnvironmentConfig = {
  ...defaultConfig,
  ...(environmentOverrides[env] || {}),
};

// Validation
if (!config.apiUrl) {
  console.error('API URL is not configured');
}

// Export helper functions
export const isDevelopment = () => config.environment === 'development';
export const isProduction = () => config.environment === 'production';
export const isStaging = () => config.environment === 'staging';
export const isTest = () => config.environment === 'test';
export const shouldUseMockData = () => false; // Deprecated - always use real API
export const isDebugEnabled = () => config.enableDebugLogging;

// Log configuration in development
if (isDevelopment() && isDebugEnabled()) {
  console.log('Environment Configuration:', {
    environment: config.environment,
    apiUrl: config.apiUrl,
    mode: 'Production API',
    version: config.appVersion,
  });
}